<template>
    <div class="wt-container">
        <p v-if="geographic && !assessment.crop" class="introduction-text">
            Please select a crop or a crop category.
        </p>
        <p v-else-if="geographic && (assessment.crop.cropID > 10000)" class="introduction-text">
            This function does not work at the sector level. Please select a crop or a crop category.
        </p>
        <p v-else-if="geographic && (assessment.crop.cropID == 1009)" class="introduction-text">
            This function does not work for fodder crops. Please select another crop or crop category.
        </p>
        <template v-else>
            <p class="introduction-text text-center">
                Efficiency and sustainability of the {{assessment.viewColor}} water footprint components of
                <span v-if="geographic">{{assessment.crop.name.toLowerCase()}} in {{assessment.getCountryOrBasinName(true)}},</span>
                <span v-else>your commodity, {{assessment.scenario.name}},</span>
                per {{countryOrBasinText}}.
            </p>
            <Table :columns="columns" :rows="rows" :fileName="fileName"></Table>
            <transition name="fade">
                <div v-if="loading" class="lottie">
                    <Lottie></Lottie>
                </div>
            </transition>
        </template>
    </div>
</template>

<script>
import Table from "@/components/shared/Table.vue";
import Lottie from "@/components/shared/Lottie.vue";
import { assessment } from "@/services/assessment-model.js";
import { countryBasinCrops, countryBasinCropsLoader, scenarioCountryBasinCrops, scenarioCountryBasinCropsLoader, getCountry, getBasin, getCrop } from "@/services/entity-model.js";
import { getFunctionGetCheckImage, getFunctionGetPercentageString, getFunctionGetWFString, getWFtString, getWSString } from "@/services/data-tools.js";

export default {
    name: "GeographicSustainTableCt",
    components: {
        Table,
        Lottie
    },
    data() {
        return {
            assessment: assessment,
            countryBasinCrops: countryBasinCrops,
            scenarioCountryBasinCrops: scenarioCountryBasinCrops
        }
    },
    computed: {
        geographic() {
            return assessment.isGeographic();
        },
        countryOrBasinText: function() {
            if (!this.geographic) {
                return "crop, country and river basin";
            }
            const entities = [];
            if ((assessment.crop.level < 4) && (assessment.crop.cropID != 1009)) {
                entities.push("crop");
            }
            if ((assessment.assessmentType.name != "countries") || (assessment.countriesOrBasins.length > 1)) {
                entities.push("country");
            }
            if ((assessment.assessmentType.name != "basins") || (assessment.countriesOrBasins.length > 1)) {
                entities.push("river basin");
            }
            if (entities.length == 3) {
                return "crop, country and river basin";
            }
            return entities.join(" and ");
        },
        loading() {
            if (!this.geographic) {
                return scenarioCountryBasinCropsLoader.status.loading;
            }
            return countryBasinCropsLoader.status.loading;
        },
        rows: function() {
            const g = this.geographic;
            const color = this.assessment.viewColor;
            const color1 = color[0].toUpperCase() + color.substr(1);
            const multiColor = color.replace("blue", "greenBlue");

            return (g? this.countryBasinCrops: this.scenarioCountryBasinCrops).filter(val =>
                (val[(g? color: ("countryBasinCrop" + color1)) + "WF"] > 0) &&
                (val[multiColor + "WFt"] > 0)
            );
        },
        columns: function() {
            const g = this.geographic;
            const color = this.assessment.viewColor;
            const color1 = color[0].toUpperCase() + color.substr(1);
            const multiColor = color.replace("blue", "greenBlue");
            const multiColor1 = color1.replace("Blue", "Green-Blue");

            const columns = [
                { key: "cropID",                                           label: "Crop",                     lookupFunction: val => getCrop(val).name },
                { key: "countryID",                                        label: "Country",                  lookupFunction: val => getCountry(val).name },
                { key: "basinID",                                          label: "River Basin",              lookupFunction: val => getBasin(val)?.name,          dataFunction: val => val || "(unknown basin)" },
                { key: (g ? color : ("countryBasinCrop" + color1)) + "WF", label: color1 + " WF (m³/yr)",                                                          dataFunction: getFunctionGetWFString("yr") },
                { key: color + "WFFraction",                               label: "% of " + color1 + " WF in Total Selection",                                     dataFunction: getFunctionGetPercentageString(1),       classes: ["left-before-row"] }
            ];

            if (!this.geographic) {
                columns.push(
                    { key: color + "WFFraction2",                              label: "% of " + color1 + " WF in Crop and Country",                                    dataFunction: getFunctionGetPercentageString(1),       classes: ["left-before-row"] }
                );
            }

            columns.push(
                { key: multiColor + "WFt",                                 label: multiColor1 + " WF (m³/t)",                                                      dataFunction: getWFtString,                            classes: ["group", "left-row"] },
                { key: multiColor + "WFt20",                               label: multiColor1 + " Benchmark",                                                      dataFunction: getWFtString,                            classes: ["group"] },
                { key: multiColor + "WFtExcess",                           label: "Deviation from Benchmark",                                                      dataFunction: getFunctionGetPercentageString(-1),      classes: ["group"] },
                { key: multiColor + "WFtSpot",                             label: "Efficiency",               exportFunction: val => val ? "NOT EFFICIENT" : "",   dataFunction: getFunctionGetCheckImage("Efficient"),   classes: ["group", "right-row"] },
                { key: "whiteSpace",                                       label: "",                                                                                                                                     classes: ["white-space"] },
                { key: (g ? color : ("basin" + color1)) + "WS",            label: (color == "blue") ? "Blue Water Scarcity" : "Water Pollution Level",             dataFunction: getWSString,                             classes: ["group", "left-row"] },
                { key: color + "WSSpot",                                   label: "Sustainability",           exportFunction: val => val ? "NOT SUSTAINABLE" : "", dataFunction: getFunctionGetCheckImage("Sustainable"), classes: ["group", "right-row"] }
            );

            return columns;
        },
        fileName() {
            if (!this.geographic) {
                return "wf-sustainability-" + assessment.viewColor + "-" + assessment.scenario.name;
            }
            if (assessment.assessmentType.name == "crops") {
                return "wf-sustainability-" + assessment.viewColor + "-" + assessment.crop.name;
            }
            if (assessment.countriesOrBasins.length >= 1) {
                return "wf-sustainability-" + assessment.viewColor + assessment.countriesOrBasins.map(countryOrBasin => "-" + countryOrBasin.name) + "-" + assessment.crop.name;
            }
            return "wf-sustainability-" + assessment.viewColor + "-the-world-" + assessment.crop.name;
        }
    }
}
</script>

